import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { HotTableRegisterer } from "@handsontable/angular";
import Handsontable from 'handsontable';
import { SheetHelper } from './sheet-helper';
import { CellStyleManager } from './cell-style-manager';
import { InputdialogComponent } from '../inputdialog/inputdialog.component';

export interface SheetOperationsDependencies {
    excels: any;
    hot: Handsontable;
    project: any;
    dataset: any[][];
    cellformulas: any[][];
    afs: AngularFirestore;
    http: HttpClient;
    dialog: MatDialog;
    toastr: ToastrService;
    customerid: string;
    projectid: string;
    sheetid: number;
    isEditMode: boolean;
    tdstyles: any[];
    refs: any;
    curSelectedRow: number;
    cellStyleManager: CellStyleManager;
}

@Injectable({
    providedIn: 'root'
})
export class SheetOperationsManager {
    private dependencies: SheetOperationsDependencies;

    constructor() { }

    public setDependencies(deps: SheetOperationsDependencies) {
        this.dependencies = deps;
    }

    private get deps() {
        if (!this.dependencies) {
            throw new Error('SheetOperationsManager dependencies not set. Call setDependencies() first.');
        }
        return this.dependencies;
    }

    /**
     * Switch to a different sheet by index
     */
    async setSheetIndex(event: any, sheetIndex: number): Promise<{ dataset: any[][], cellformulas: any[][], datasetids: any[] }> {
        console.log('setSheetIndex ', sheetIndex, event);

        const deps = this.deps;
        const PROJECT = Object.keys(deps.excels)[0]; // Get first excel key as PROJECT

        const t0 = performance.now();
        const promisesresult = await deps.excels[PROJECT].getSheetByIndex(sheetIndex);
        const index = promisesresult.index;
        const dataset = this.handleCellErrors(promisesresult.cells);
        const datasetids = promisesresult.datasetids;
        const cellformulas = promisesresult.cellvalues;

        console.log('dataset for new sheet:', dataset);
        const t1 = performance.now();
        console.log("getSheetByIndex took " + (t1 - t0) + " milliseconds.");

        // Get cell styles for the new sheet
        await this.getCellStyles(sheetIndex);
        console.log('tdstyles after getCellStyles in setSheetIndex:', JSON.stringify(deps.tdstyles));

        // Update current sheet ID in excel
        deps.excels[PROJECT].setCurrentSheetId(sheetIndex);

        return { dataset, cellformulas, datasetids };
    }

    /**
     * Add a new sheet to the project
     */
    async addNewSheet(): Promise<{ sheetId: number; sheetName: string }> {
        console.log('addNewSheet');
        const deps = this.deps;
        const PROJECT = Object.keys(deps.excels)[0];

        try {
            // Get the next sheet number
            const sheetNumber = deps.project.sheets.length;
            const sheetName = `Sheet${sheetNumber + 1}`;

            const firebaseFunctionUrl = 'https://us-central1-xbuild3d.cloudfunctions.net/addSheet';
            const body = {
                customerid: deps.customerid,
                projectid: deps.projectid,
                sheetNumber: sheetNumber
            };

            deps.http.post(firebaseFunctionUrl, body).toPromise();

            const result = await deps.excels[PROJECT].addSheet(sheetName);
            const { sheetId } = result;

            deps.project.sheets.push({
                name: sheetName,
                visible: false,
                colWidths: new Array(26).fill(100) // Initialize column widths for A-Z
            });

            return { sheetId, sheetName };
        } catch (error) {
            console.error('Error adding sheet:', error);
            deps.toastr.error(error.message || 'Error adding sheet');
            throw error;
        }
    }

    /**
     * Copy formulas for a sheet
     */
    async copyFormulas(sheetIndex: number, event: any): Promise<void> {
        console.log('copyFormulas', sheetIndex);
        const deps = this.deps;
        const PROJECT = Object.keys(deps.excels)[0];

        // Use Utils.copyFormulas if available, otherwise implement the logic
        if (typeof (window as any).Utils?.copyFormulas === 'function') {
            await (window as any).Utils.copyFormulas(deps.excels[PROJECT], sheetIndex);
        }
        event.stopPropagation();
    }

    /**
     * Clear all data in a sheet
     */
    async clearSheet(sheetIndex: number, event: any): Promise<{ dataset: any[][], cellformulas: any[][] }> {
        console.log('clearSheet', sheetIndex);
        const deps = this.deps;
        const PROJECT = Object.keys(deps.excels)[0];

        await deps.excels[PROJECT].clearSheet(sheetIndex);
        const dataset = await deps.excels[PROJECT].getSheetValues(sheetIndex);
        const cellformulas = await deps.excels[PROJECT].getCellFormulas(sheetIndex);

        return { dataset, cellformulas };
    }

    /**
     * Rename a sheet
     */
    renameSheet(sheetIndex: number, event: any): void {
        console.log('renameSheet', sheetIndex);
        const deps = this.deps;
        const PROJECT = Object.keys(deps.excels)[0];

        const dialogRef = deps.dialog.open(InputdialogComponent, {
            width: '400px',
            data: {
                value: deps.project.sheets[sheetIndex].name,
                okbutton: "Rename",
                head: "Rename Sheet",
                property: "Sheet Name"
            },
        });

        dialogRef.afterClosed().subscribe(result => {
            console.log('The dialog was closed', result);

            if (result) {
                deps.excels[PROJECT].renameSheet(sheetIndex, result);
                deps.project.sheets[sheetIndex].name = result;
            }
        });

        event.stopPropagation();
    }

    /**
     * Remove a sheet (placeholder - implement as needed)
     */
    removeSheet(sheetIndex: number, event: any): void {
        console.log('removeSheet', sheetIndex);
        event.stopPropagation();
        // TODO: Implement sheet removal logic
    }

    /**
     * Resize tables with additional columns and rows
     */
    resizeTables(additionalColumns: number, additionalRows: number): void {
        const deps = this.deps;

        const firebaseFunctionUrl = 'https://us-central1-xbuild3d.cloudfunctions.net/resizeTables';
        const headers = new HttpHeaders({
            'Content-Type': 'application/json'
        });

        const body = {
            customerid: deps.customerid,
            projectid: deps.projectid,
            additionalColumns: additionalColumns,
            numberOfRows: additionalRows,
            sheetscount: deps.project.sheets.length
        };

        deps.http.post(firebaseFunctionUrl, body, { headers }).subscribe(result => {
            console.log('resizeTables', result);
            window.location.reload();
        });
    }

    /**
     * Apply cell metadata and styling
     */
    async applyCellMeta(): Promise<void> {
        const deps = this.deps;
        const PROJECT = Object.keys(deps.excels)[0];

        // Convert Excel-style refs (A1, B3 etc) to HyperFormula simple cell references
        if (deps.refs) {
            // Map string refs to array if needed
            if (Array.isArray(deps.refs) && deps.refs.length > 0 && typeof deps.refs[0] === 'string') {
                let newRefs = [];
                for (const ref of deps.refs) {
                    try {
                        const simpleAddress = await deps.excels[PROJECT].simpleCellAddressFromString(ref, deps.sheetid);
                        if (simpleAddress) {
                            newRefs.push(simpleAddress);
                        }
                    } catch (err) {
                        console.warn("Error converting cell reference:", err);
                    }
                }
                deps.refs = newRefs;
            }
        }

        deps.cellStyleManager.applyCellMetaStyles(
            deps.hot,
            deps.dataset,
            deps.sheetid,
            deps.isEditMode,
            deps.refs,
            deps.curSelectedRow,
            () => SheetHelper.getOutputRow(deps.dataset)
        );
    }

    /**
     * Get cell styles for a specific sheet
     */
    private async getCellStyles(sheetid: number): Promise<void> {
        const deps = this.deps;

        try {
            // Clear existing styles from all cells first
            if (deps.hot) {
                const rowCount = deps.hot.countRows();
                const colCount = deps.hot.countCols();
                for (let r = 0; r < rowCount; r++) {
                    for (let c = 0; c < colCount; c++) {
                        try {
                            const meta = deps.hot.getCellMeta(r, c);
                            if (meta.stylemeta) {
                                // Reset background and foreground styles
                                meta.stylemeta.background = undefined;
                                meta.stylemeta.foreground = undefined;
                                deps.hot.setCellMeta(r, c, 'stylemeta', meta.stylemeta);
                            }
                        } catch (error) {
                            console.warn("Error resetting cell style:", error);
                        }
                    }
                }
            }

            const snapshot = await deps.afs.collection(
                "/projects/userprojects/" + deps.customerid + "/" + deps.projectid + "/tdstyles/",
                ref => ref.where("sheetid", "==", sheetid).orderBy("datetime")
            ).get().toPromise();

            deps.tdstyles = snapshot.docs.map(doc => ({ id: doc.id, ...(doc.data() as object) }));

            for (let y = 0; y < deps.tdstyles.length; y++) {
                const s = JSON.parse(deps.tdstyles[y].s);
                const e = deps.tdstyles[y].e;

                for (var i = s[0][0]; i <= s[0][2]; i++) {
                    for (var j = s[0][1]; j <= s[0][3]; j++) {
                        var cell = deps.hot.getCell(i, j);

                        try {
                            var meta = deps.hot.getCellMeta(i, j).stylemeta || {};
                        } catch (error) {
                            meta = {};
                        }

                        if (e.background) {
                            meta.background = e.background;
                        }
                        if (e.foreground) {
                            meta.foreground = e.foreground;
                        }

                        try {
                            deps.hot.setCellMeta(i, j, 'stylemeta', meta);
                        } catch (error) {
                            console.warn(error);
                        }
                    }
                }
            }

            if (deps.hot) {
                deps.hot.render();
            } else {
                console.warn("Handsontable instance is undefined. Cannot render.");
            }
        } catch (error) {
            console.error("Error getting documents: ", error);
        }
    }

    /**
     * Handle cell errors
     */
    private handleCellErrors(cells: any[][]): any[][] {
        for (var i = 0; i < cells.length; i++) {
            for (var j = 0; j < cells[i].length; j++) {
                cells[i][j] = this.handleCellError(cells[i][j]);
            }
        }
        return cells;
    }

    /**
     * Handle individual cell error
     */
    private handleCellError(cell: any): any {
        if (cell?.type == 'VALUE' || cell?.type == 'ERROR') {
            // address = 'Sheet2!D9'
            // message = 'Value cannot be coerced to number.'
            // type = 'VALUE'
            // value = '#VALUE!'
            return "#." + cell?.message;
        }
        return cell;
    }

    /**
     * Set cell background color
     */
    setCellBackground(color: any): void {
        const deps = this.deps;
        deps.project.settings.stylesoverride = true;
        deps.cellStyleManager.setCellBackground(
            deps.hot,
            deps.dataset,
            deps.sheetid,
            deps.customerid,
            deps.projectid,
            color
        );
    }

    /**
     * Reset cell background color
     */
    resetCellBackground(): void {
        const deps = this.deps;
        deps.project.settings.stylesoverride = true;
        deps.cellStyleManager.resetCellBackground(
            deps.hot,
            deps.dataset,
            deps.sheetid,
            deps.customerid,
            deps.projectid
        );
    }

    /**
     * Set cell foreground color
     */
    setCellForeground(color: any): void {
        const deps = this.deps;
        deps.project.settings.stylesoverride = true;
        deps.cellStyleManager.setCellForeground(
            deps.hot,
            deps.dataset,
            deps.sheetid,
            deps.customerid,
            deps.projectid,
            color
        );
    }

    /**
     * Reset cell foreground color
     */
    resetCellForeground(): void {
        const deps = this.deps;
        deps.project.settings.stylesoverride = true;
        deps.cellStyleManager.resetCellForeground(
            deps.hot,
            deps.dataset,
            deps.sheetid,
            deps.customerid,
            deps.projectid
        );
    }

    /**
     * Toggle styles override setting
     */
    stylesoverride(isEnabled: boolean): void {
        const deps = this.deps;
        console.log('stylesoverride', isEnabled, deps.project?.settings?.stylesoverride);
        deps.project.settings.stylesoverride = isEnabled;

        if (deps.isEditMode) {
            deps.hot?.render();
        }
    }
} 